/**
 * A descrição da tarefa — o que ela é, em texto livre.
 *
 * <p>Faltava o campo mais óbvio de todos. O card tinha nome, responsáveis, checklist e
 * conversa, e nenhum lugar para dizer <b>o que é</b> a tarefa. O que acontecia era o
 * previsível: ou o nome virava a descrição ("Ajustes [GG] Pilot (dashboard) - trocar a
 * cor do header e arrumar o menu"), ou o contexto ia para o primeiro comentário, onde
 * afunda assim que a conversa anda.
 *
 * <p><b>Lê como texto, edita ao clicar.</b> Uma caixa de edição sempre aberta ocupa o
 * dobro do espaço e transforma "ler o card" em "olhar um formulário" — e a descrição é
 * lida muito mais vezes do que escrita. Fechada, ela mostra os links clicáveis e as
 * menções realçadas; aberta, é texto puro, que é o que se edita.
 *
 * <p>Salva ao sair do campo, e não num botão: o modal inteiro é assim (título, coluna,
 * responsáveis), e um botão só aqui faria a descrição parecer a única coisa que precisa
 * ser confirmada.
 */
import { useEffect, useRef, useState } from 'react';

import type { Membro } from '../api/tipos';
import { linhasDaDescricao } from './alturaDaDescricao';
import { CaixaComMencao } from './CaixaComMencao';
import { TextoRico } from './TextoRico';
import './descricao.css';

export function DescricaoDaTarefa({
  descricao,
  membros,
  aoSalvar,
}: {
  descricao: string | null | undefined;
  membros: Membro[];
  aoSalvar(texto: string): void;
}) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(descricao ?? '');
  const valorAoAbrir = useRef('');

  // O que veio do servidor manda enquanto ninguém está digitando: sem isto, salvar e
  // reabrir o card mostraria o rascunho velho por cima do texto novo.
  useEffect(() => {
    if (!editando) setRascunho(descricao ?? '');
  }, [descricao, editando]);

  function abrir() {
    valorAoAbrir.current = descricao ?? '';
    setRascunho(descricao ?? '');
    setEditando(true);
  }

  function fechar() {
    setEditando(false);
    // Só chama o servidor se mudou: sair do campo sem digitar nada não é uma edição, e
    // gravar assim mesmo encheria a trilha de atividade de ruído.
    const novo = rascunho.trim();
    if (novo !== valorAoAbrir.current.trim()) aoSalvar(novo);
  }

  const texto = (descricao ?? '').trim();

  return (
    <div className="ds">
      <label className="ds-rot">Descrição</label>

      {editando ? (
        <>
          <CaixaComMencao
            valor={rascunho}
            aoMudar={setRascunho}
            membros={membros}
            autoFocus
            /*
             * A caixa abre do tamanho do texto, e não em cinco linhas fixas.
             *
             * Fechada, a descrição mostra tudo e cresce com o conteúdo; aberta, ela caía
             * numa janela de cinco linhas com barra de rolagem. Clicar para editar
             * ENCOLHIA o que se estava lendo — o contrário do que a ação promete. O
             * cálculo, com piso e teto, está em `alturaDaDescricao`.
             */
            rows={linhasDaDescricao(rascunho)}
            placeholder="O que é esta tarefa? Contexto, links, o que precisa estar pronto…"
            aoSairDoCampo={fechar}
            aoTeclar={(e) => {
              // Esc desiste; Ctrl+Enter salva. Enter sozinho quebra linha, porque isto é
              // texto longo — e Enter salvando manda descrição pela metade.
              if (e.key === 'Escape') {
                setRascunho(valorAoAbrir.current);
                setEditando(false);
              }
              // Chama `fechar` direto, e não `blur()`: o blur é o caminho que já falhou
              // uma vez, e depender dele aqui seria depender duas vezes da mesma coisa.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) fechar();
            }}
          />

          {/*
            * Botão explícito, além do salvar ao sair do campo.
            *
            * A descrição já sumiu uma vez em produção: o `onBlur` que salvava era
            * engolido pelo do CaixaComMencao, e o texto digitado ia embora sem aviso.
            * O conserto daquilo está feito — mas salvar por um evento INVISÍVEL é
            * frágil por natureza: quem não sabe que existe não sabe que funcionou, e
            * quando não funciona ninguém tem como perceber antes de perder o texto.
            *
            * Com o botão, salvar é uma coisa que se vê e se clica. O blur continua
            * salvando para quem só clica fora — mas deixou de ser o único caminho.
            */}
          <div className="ds-acoes">
            <button type="button" className="ds-salvar" onMouseDown={(e) => e.preventDefault()} onClick={fechar}>
              Salvar
            </button>
            <button
              type="button"
              className="ds-cancelar"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setRascunho(valorAoAbrir.current);
                setEditando(false);
              }}
            >
              Cancelar
            </button>
            <span className="ds-dica">Ctrl+Enter salva · Esc desiste</span>
          </div>
        </>
      ) : (
        <div
          className={`ds-texto${texto ? '' : ' vazio'}`}
          role="button"
          tabIndex={0}
          onClick={abrir}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              abrir();
            }
          }}
          title="Clique para editar"
        >
          {texto ? (
            <TextoRico texto={texto} membros={membros} />
          ) : (
            'Sem descrição. Clique para escrever o que é esta tarefa.'
          )}
        </div>
      )}
    </div>
  );
}
